import { format } from "date-fns";
import { isRight } from "fp-ts/lib/Either";
import i18next from "i18next";
import { inject, observer } from "mobx-react";
import { Fragment, Component, SyntheticEvent } from "react";
import { Translation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { UserIcon } from "../../components/icons/UserIcon.component";
import {
  AnonymousRecipient,
  PostUp,
  PostUpList,
  PostUpListResolver,
  Recipient,
  RecipientListResolver,
  RecipientResolver,
  Station,
  StationListResolver,
} from "../../decoders/PostUp.types";
import type { BackendSessionState, Store } from "@inferno/renderer-shared-core";
import { isRecipientResponse } from "../../lib/guards";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { IHeartUserProfile } from "@inferno/renderer-shared-core";
import {
  getRecipient,
  getRecipientSubscriptions,
  getStation,
  POSTUP_RECIPIENT_CHANNEL,
  POSTUP_RECIPIENT_SOURCE,
  sendRecipient,
  sendSubscriptionLists,
} from "../../services/PostUp";
import { Button, Container, MagicLink } from "../../ui";
import { ButtonKind } from "../../ui";
import type { PageBlockInterface } from "../Block.component";
import "./Newsletter.style.scss";

const log = ILog.logger("Subscribe");

interface SubscribeProps extends PageBlockInterface {
  store?: Store;
}

interface SubscribeState {
  loggedIn: boolean;
  stationLists?: Station[];
  profile?: IHeartUserProfile | null;
  subscriber?: Recipient | null;
  subscriberLists?: PostUpList;
}

@inject("store")
@observer
export class Subscribe extends Component<SubscribeProps, SubscribeState> {
  private saveInProgress = false;
  mounted = false;
  toastId = "subscriptions-update-notify";

  constructor(props: SubscribeProps) {
    super(props);

    this.state = {
      loggedIn: false,
      subscriberLists: [],
    };
  }

  private async update(state: BackendSessionState) {
    if (!this.props.store) {
      return;
    }

    const { site, api_base_uri } = this.props.store;

    if (site && api_base_uri && state) {
      let subscriber = null;
      let lists: PostUpList = [];

      if (state.authenticated && state.profile) {
        const subData = await getRecipient({
          slug: site.index.slug,
          base_uri: api_base_uri || "",
          email: state.profile.email,
        });

        const validatedRecipient = RecipientListResolver.decode(subData);
        subscriber = isRight(validatedRecipient) ? validatedRecipient.right[0] : null;

        if (subscriber) {
          const listData = await getRecipientSubscriptions({
            slug: site.index.slug,
            base_uri: api_base_uri,
            recipientId: subscriber.recipientId,
          });

          const validatedSubscriptions = PostUpListResolver.decode(listData);
          if (isRight(validatedSubscriptions)) {
            lists = validatedSubscriptions.right;
          }
        }
      }

      if (this.mounted) {
        this.setState({
          loggedIn: state.authenticated,
          profile: state.profile || null,
          subscriber,
          subscriberLists: lists,
        });
      }
    }
  }

  dismiss = () => toast.dismiss(this.toastId);

  login = () => {
    this.props.store?.onAuthAction.dispatch({
      action: "pending",
    });

    this.props.store?.session?.currentSession?.login();
  };

  logout = () => {
    this.props.store?.session?.currentSession?.logout();
  };

  signup = () => {
    this.props.store?.onAuthAction.dispatch({
      action: "pending",
    });

    this.props.store?.session?.currentSession?.signup();
  };

  save = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!this.props.store || this.saveInProgress) {
      return;
    }

    this.saveInProgress = true;

    const { profile } = this.state;
    if (!profile || !profile.email || !this.state.subscriberLists) {
      return null;
    }

    const userName = (profile.name || profile.email || "").split("@")[0];
    let birthDate = "";
    if (profile.birthYear && profile.birthMonth && profile.birthDay) {
      birthDate = profile.birthMonth + "/" + profile.birthDay + "/" + profile.birthYear;
    }

    const { site, api_base_uri } = this.props.store;

    const recipient: AnonymousRecipient = {
      ...this.state.subscriber,
      address: profile.email,
      externalId: profile.email,
      channel: POSTUP_RECIPIENT_CHANNEL,
      sourceDescription: POSTUP_RECIPIENT_SOURCE,
      demographics: [
        `FirstName=${userName}`,
        `_PostalCode=${profile.zipCode || ""}`,
        `_Gender=${profile.gender || ""}`,
        `_DateOfBirth=${birthDate}`,
      ],
    };

    try {
      toast.info(i18next.t("subscriptions_success"), {
        autoClose: 10000,
        toastId: this.toastId,
      });

      const recipientResponse = await sendRecipient({
        slug: site.index.slug,
        base_uri: api_base_uri || "",
        recipient,
      });

      const validatedRecipient = RecipientResolver.decode(recipientResponse);
      const recipientData = isRight(validatedRecipient) ? validatedRecipient.right : null;

      if (isRecipientResponse(recipientData)) {
        // add recipientId to subscriberLists for new subscriber
        const subscriberData = recipientData as Recipient;
        const subscriberLists = this.state.subscriberLists.map((list: PostUp) => {
          if (list) {
            list.recipientId = subscriberData.recipientId;
          }
          return list;
        });

        if (this.mounted) {
          this.setState({
            subscriber: subscriberData,
            subscriberLists,
          });
        }

        const subscriptionListResponse = await sendSubscriptionLists({
          slug: site.index.slug,
          base_uri: api_base_uri || "",
          lists: this.state.subscriberLists,
        });

        if (subscriptionListResponse) {
          log.info("Subscriber data updated");
          this.saveInProgress = false;
        }
      } else {
        // dismiss the update toast
        this.dismiss();

        // null response means there's some error
        toast.info(i18next.t("subscriptions_error"), {
          autoClose: 3000,
          toastId: "subscriptions-error-notify",
        });
        log.error(`An error occurred while updating Recipient: ${recipientData} `);
        this.saveInProgress = false;
      }
    } catch (err) {
      log.error("An error occurred while updating subscriber data: ", err);
    }
  };

  async componentDidMount() {
    this.mounted = true;
    if (!this.props.store) {
      return;
    }

    this.props.store.storeBlock(this.props.block);

    const { site, session, api_base_uri } = this.props.store;

    if (site && session && api_base_uri) {
      if (session.currentSession) {
        session.currentSession.onStatusChanged.subscribe(state => this.update(state));
      }

      const stationData = await getStation({
        slug: site.index.slug,
        base_uri: api_base_uri,
      });

      const validatedStations = StationListResolver.decode(stationData.lists);
      if (!isRight(validatedStations)) {
        log.error("Could not validate newsletters", getPaths(validatedStations), stationData.lists);
      }
      const stationLists = isRight(validatedStations) ? validatedStations.right : [];

      if (this.mounted) {
        this.setState(
          {
            stationLists,
          },
          () => {
            if (session.currentSession) {
              this.update(session.currentSession.state);
            }
          },
        );
      }
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  static checkListStatus = (listId: number, lists: PostUpList) => {
    if (!listId || !lists) {
      return false;
    }

    let checked = false;
    lists.map((list: PostUp) => {
      if (listId === list.listId && list.status === "NORMAL") {
        checked = true;
      }
    });

    return checked;
  };

  private changeListStatus = (event: SyntheticEvent) => {
    const target = event.target as HTMLInputElement;

    if (target) {
      let subscriberLists: any = [];
      if (this.state.subscriberLists && this.state.subscriberLists.length) {
        // for existing subscriber list
        subscriberLists = this.state.subscriberLists.map((list: PostUp) => {
          if (list) {
            if (target.value === list.listId.toString()) {
              list.status = !target.checked ? "UNSUB" : "NORMAL";
            }
          }
          return list;
        });
      } else if (this.state.stationLists && this.state.stationLists.length) {
        // first time subscriptions
        const { subscriber } = this.state;
        subscriberLists = this.state.stationLists.map((list: Station) => {
          let status = "UNSUB";
          if (target.value === list.listId.toString()) {
            status = !target.checked ? "UNSUB" : "NORMAL";
          }
          const formatted = format(new Date(), "yyyy-MM-dd HH:mm:ss", {
            useAdditionalWeekYearTokens: true,
          });
          // convert stationList to subscribeList
          return {
            listId: list.listId,
            recipientId: subscriber ? subscriber.recipientId : "",
            status,
            dateJoined: formatted,
            address: subscriber ? subscriber.address : "",
            sourceId: "newsletter",
            confirmed: true,
          };
        });
      }
      const latestSubscriberLists = subscriberLists as PostUpList;

      if (this.mounted) {
        this.setState({ subscriberLists: latestSubscriberLists });
      }
    }
  };

  render() {
    if (!this.props.store) {
      return null;
    }

    const { site } = this.props.store;
    const { profile } = this.state;
    if (!this.state.loggedIn || !profile) {
      return (
        <Container className="component-newsletter">
          <Translation>
            {t => (
              <div className="description">
                {t("subscriptions_login_desc", {
                  station: `${site.sections.general?.name}`,
                })}
                <button onClick={this.signup}>{t("subscriptions_signup")}</button>
                {t("subscriptions_login_cta")}
              </div>
            )}
          </Translation>
          <Translation>
            {t => (
              <div className="button-wrapper">
                <Button className={"login-btn"} kind={ButtonKind.PRIMARY} click={this.login}>
                  {t("login")}
                </Button>
                <Button className={"signup-btn"} kind={ButtonKind.SECONDARY} click={this.signup}>
                  {t("subscriptions_signup")}
                </Button>
              </div>
            )}
          </Translation>
        </Container>
      );
    }

    const userName = (profile.name || profile.email || "").split("@")[0];

    let birthDate = "";
    if (profile.birthYear && profile.birthMonth && profile.birthDay) {
      birthDate = profile.birthMonth + " / " + profile.birthDay + " / " + profile.birthYear;
    } // if

    const profilePic = profile.profileId ? (
      <MagicLink to={`${this.props.store?.env.NATIONAL_SITE_URL}/my/settings/`} target="_blank">
        <img
          className="profile-pic lazyload"
          alt="Profile Image"
          data-src={`//i.iheart.com/v3/user/${profile.profileId}/profile?ops=fit(88%2C88)`}
        />
      </MagicLink>
    ) : (
      <UserIcon className="profile-pic" />
    );

    return (
      <Fragment>
        <Container className="component-newsletter">
          <Translation>
            {t => (
              <div className="login-as">
                {t("subscriptions_loggedin_as")}
                {userName}
                {t("subscriptions_notyou")}
                <button onClick={this.logout}>{t("subscriptions_logout")}</button>
              </div>
            )}
          </Translation>
          <Translation>
            {t => (
              <div className="description">
                {t("subscriptions_update_desc").trimRight()}&nbsp;
                <MagicLink to={`${this.props.store?.env.NATIONAL_SITE_URL}/my/settings/`} target="_blank">
                  {t("subscriptions_update_settings")}
                </MagicLink>
              </div>
            )}
          </Translation>
          <section className="user">
            <form role="form" noValidate={true}>
              <div className="profile">
                <div className="user-image-wrapper">{profilePic}</div>
                <div className="user-profile-details">
                  <div className="profile-row">
                    <span className="label">{i18next.t("name")}</span>
                    <span className="data">{userName}</span>
                  </div>
                  <div className="profile-row">
                    <span className="label">{i18next.t("email")}</span>
                    <span className="data">{profile.email}</span>
                  </div>
                  <div className="profile-row password">
                    <span className="label">{i18next.t("password")}</span>
                    <span className="data">&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;</span>
                  </div>
                  {profile.gender ? (
                    <div className="profile-row">
                      <span className="label">{i18next.t("gender")}</span>
                      <span className="data">{profile.gender}</span>
                    </div>
                  ) : null}
                  {profile.phoneNumber ? (
                    <div className="profile-row">
                      <span className="label">{i18next.t("phone")}</span>
                      <span className="data">{profile.phoneNumber}</span>
                    </div>
                  ) : null}
                  {profile.zipCode ? (
                    <div className="profile-row">
                      <span className="label">{i18next.t("zip")}</span>
                      <span className="data">{profile.zipCode}</span>
                    </div>
                  ) : null}
                  {birthDate ? (
                    <div className="profile-row">
                      <span className="label">{i18next.t("birthdate")}</span>
                      <span className="data">{birthDate}</span>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="options">
                <Translation>{t => <div className="options-header">{t("subscriptions_options")}</div>}</Translation>
                <ul className="station-lists">
                  {this.state.stationLists?.map((list: Station, index: number) => {
                    if (list.publicSignup) {
                      return (
                        <li key={index}>
                          <input
                            type="checkbox"
                            name={`stationsLists[${index}]`}
                            id={`station-list-${list.listId}`}
                            value={list.listId}
                            defaultChecked={Subscribe.checkListStatus(list.listId, this.state.subscriberLists || [])}
                            onChange={this.changeListStatus}
                            aria-labelledby={`station-list-label-${list.listId}`}
                          />
                          <label htmlFor={`station-list-${list.listId}`} id={`station-list-label-${list.listId}`}>
                            {list.friendlyTitle || list.title}
                          </label>
                        </li>
                      );
                    }
                  })}
                </ul>
              </div>
              <Translation>
                {t => (
                  <div className="button-wrapper">
                    <Button className={"save-btn"} kind={ButtonKind.PRIMARY} click={this.save}>
                      {t("subscriptions_save")}
                    </Button>
                  </div>
                )}
              </Translation>
            </form>
          </section>
        </Container>
        <ToastContainer autoClose={5000} position="bottom-center" />
      </Fragment>
    );
  }
}

export default Subscribe;
